import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { MediaLinkModel, MediaLinkRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['mediaLinks']

type MediaLinksFilters = {
  linkable_type?: string,
  linkable_id?: number,
}

type UseMediaLinksOptions = {
  filters?: MediaLinksFilters,
  performHttpRequests?: boolean,
  requestOptions?: MediaLinkRequestOptions,
}

function useMediaLinks(options: UseMediaLinksOptions) {
  const { filters = {}, requestOptions } = options

  const {
    linkable_id: filterLinkableId,
    linkable_type: filterLinkableType,
  } = filters

  const {
    updatedEntities: { mediaLinks: mediaLinksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { mediaLinks } = useSelector(reduxState => reduxState.entities)

  const filteredMediaLinks = useMemo(() => {
    const filtered = Object.values(mediaLinks).filter((mediaLink: MediaLinkModel) => {
      const { linkable_type, linkable_id } = mediaLink

      const matchLinkableId = matchFilterNumber(linkable_id, filterLinkableId)
      const matchLinkableType = filterLinkableType ? filterLinkableType === linkable_type : true

      return matchLinkableId && matchLinkableType
    })

    return sortArrayBy(filtered, 'asc')
  }, [mediaLinksUpdatedAt, JSON.stringify(filters)])

  const filteredMediaLinksCount = filteredMediaLinks.length
  const hasFilteredMediaLinks = !!filteredMediaLinksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredMediaLinksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingMediaLinks } = useReduxAction(
    'mediaLinks',
    'loadMediaLinks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredMediaLinks,
    filteredMediaLinksCount,
    hasFilteredMediaLinks,
    loading: loadingMediaLinks,
  }
}

export default useMediaLinks
